import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PageSEO } from "./components/PageSEO";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import AdvisorPage from "./pages/AdvisorPage";
import JobEvaluatorPage from "./pages/JobEvaluatorPage";
import SeverancePage from "./pages/SeverancePage";
import SalaryBenchmarkPage from "./pages/SalaryBenchmarkPage";
import SavedResultsPage from "./pages/SavedResultsPage";
import ProfilePage from "./pages/ProfilePage";
import GuidancePage from "./pages/GuidancePage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import NetSalaryPage from "./pages/NetSalaryPage";
import RetirementPage from "./pages/RetirementPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ReferralRedirectPage from "./pages/ReferralRedirectPage";
import AdminPage from "./pages/AdminPage";
import NotFoundPage from "./pages/NotFoundPage";

// Rutas que ya montan su propio <PageSEO> (ver cada page). react-helmet-async 3.x, bajo
// React 19, delega en el hoisting nativo de <title>/<meta>/<link> de React — que NO
// deduplica entre instancias montadas a la vez (es "undefined behavior" documentado por
// la propia librería). Por eso nunca puede haber dos <Helmet> activos al mismo tiempo: el
// default de abajo se desactiva exactamente en las rutas que ya traen el suyo, para que
// en cualquier momento exista uno solo, nunca cero ni dos.
const ROUTES_WITH_OWN_SEO = new Set([
  "/", "/severance", "/job-evaluator", "/salary-benchmark", "/net-salary", "/advisor", "/retiro",
]);

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  // This MUST be checked synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      {!ROUTES_WITH_OWN_SEO.has(location.pathname) && (
        <PageSEO
          title="Klarvi | Herramientas laborales y retiro en México"
          description="Calcula liquidación, sueldo neto y retiro. Compara salarios, evalúa ofertas y resuelve dudas laborales. Herramientas para empleados en México."
          canonical="https://klarvi.org/"
        />
      )}
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/precios" element={<PricingPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/r/:code" element={<ReferralRedirectPage />} />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      
      {/* Onboarding (protected but allows incomplete profile) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor"
        element={<AdvisorPage />}
      />
      <Route
        path="/job-evaluator"
        element={<JobEvaluatorPage />}
      />
      <Route
        path="/severance"
        element={<SeverancePage />}
      />
      <Route
        path="/salary-benchmark"
        element={<SalaryBenchmarkPage />}
      />
      <Route
        path="/net-salary"
        element={<NetSalaryPage />}
      />
      <Route
        path="/retiro"
        element={
          <ProtectedRoute>
            <RetirementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved"
        element={
          <ProtectedRoute>
            <SavedResultsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guidance"
        element={
          <ProtectedRoute>
            <GuidancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
